import React, { useEffect, useState } from "react";
import { QRCodeCanvas } from "qrcode.react";
import { toast } from "sonner";
import { CreditCard, Wallet, Receipt, AlertCircle, Copy, RefreshCw } from "lucide-react";
import api, { formatApiError } from "../../lib/api";
import { Window, Loading, EmptyState } from "../../components/VB";

const rp = (n) => "Rp " + Math.round(parseFloat(n || 0)).toLocaleString("id-ID");

export default function KoperasiSaya() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/koperasi/saya");
      setData(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  if (loading) return <Window title="Koperasi Saya"><Loading /></Window>;

  // Belum jadi anggota
  if (!data?.anggota) {
    return (
      <Window title="Koperasi Saya">
        <EmptyState message="Anda belum terdaftar sebagai anggota koperasi. Silakan hubungi admin HR." />
      </Window>
    );
  }

  const a = data.anggota;
  const plafon = data.plafon || 0;
  const terhutang = data.terhutang || 0;
  const sisa = data.sisa_plafon || 0;
  const usagePct = plafon > 0 ? Math.min(100, (terhutang / plafon) * 100) : 0;
  const barColor = usagePct >= 90 ? "#d8000c" : usagePct >= 60 ? "#e88800" : "#4f8a10";

  const copyRfid = () => {
    navigator.clipboard.writeText(a.rfid_card).then(() => toast.success("Nomor RFID disalin"));
  };

  return (
    <Window title="Koperasi Saya">
      {/* Top grid: QR + info */}
      <div style={{ display: "grid", gridTemplateColumns: "260px 1fr", gap: 12, marginBottom: 12 }}>
        {/* QR Card */}
        <div
          style={{
            background: "white",
            border: "2px solid #003399",
            padding: 14,
            textAlign: "center",
            boxShadow: "3px 3px 0 rgba(0,51,153,0.25)",
          }}
          data-testid="kop-qr-card"
        >
          <div style={{ fontSize: 11, color: "#003399", fontWeight: 700, letterSpacing: 1, marginBottom: 6 }}>
            KARTU KOPERASI DIGITAL
          </div>
          <div style={{ display: "inline-block", background: "white", padding: 8, border: "1px solid #ccc" }}>
            <QRCodeCanvas
              value={a.rfid_card || "NO-RFID"}
              size={180}
              level="H"
              includeMargin={false}
              data-testid="kop-qrcode"
            />
          </div>
          <div style={{ marginTop: 8, fontSize: 11, color: "#666" }}>Tunjukkan ke vendor koperasi</div>
          <div
            style={{
              marginTop: 6,
              fontFamily: "Consolas, monospace",
              fontSize: 14,
              fontWeight: 700,
              color: "#003399",
              userSelect: "all",
            }}
            data-testid="kop-rfid-number"
          >
            {a.rfid_card}
          </div>
          <button
            className="vb-btn"
            style={{ marginTop: 6, padding: "3px 8px", fontSize: 11 }}
            onClick={copyRfid}
            data-testid="kop-copy-rfid"
          >
            <Copy className="w-3 h-3 inline mr-1" /> Salin Nomor
          </button>
        </div>

        {/* Info panel */}
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          <div style={{ background: "#f5f5f5", border: "1px solid #ccc", padding: 10 }}>
            <div style={{ fontSize: 11, color: "#666", marginBottom: 4 }}>Nomor Rekening Koperasi</div>
            <div style={{ fontFamily: "Consolas, monospace", fontSize: 16, fontWeight: 700, color: "#003399" }} data-testid="kop-no-rekening">
              {a.no_rekening_koperasi || "-"}
            </div>
            {a.catatan && <div style={{ fontSize: 11, color: "#666", marginTop: 4, fontStyle: "italic" }}>{a.catatan}</div>}
          </div>

          {/* Plafon usage bar */}
          <div style={{ background: "#FFFFE1", border: "1px solid #b89a00", padding: 10 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", fontSize: 11, color: "#5f4300", marginBottom: 4 }}>
              <span><CreditCard className="w-3 h-3 inline mr-1" />Penggunaan Plafon</span>
              <span style={{ fontWeight: 700 }}>{usagePct.toFixed(1)}%</span>
            </div>
            <div style={{ background: "#fff", border: "1px solid #ccc", height: 14, overflow: "hidden" }}>
              <div style={{ width: `${usagePct}%`, background: barColor, height: "100%", transition: "width .4s" }} />
            </div>
            <div style={{ display: "flex", justifyContent: "space-between", fontSize: 10, color: "#666", marginTop: 3 }}>
              <span>Terpakai: {rp(terhutang)}</span>
              <span>Plafon: {rp(plafon)}</span>
            </div>
          </div>

          {/* Status */}
          {!a.status_aktif && (
            <div style={{ background: "#ffd6d6", border: "1px solid #d8000c", padding: 8, fontSize: 12, color: "#d8000c" }}>
              <AlertCircle className="w-4 h-4 inline mr-1" /> Kartu koperasi Anda saat ini <strong>tidak aktif</strong>. Transaksi tidak dapat dilakukan.
            </div>
          )}

          <button
            className="vb-btn"
            style={{ alignSelf: "flex-start", padding: "3px 10px" }}
            onClick={load}
            data-testid="kop-refresh"
          >
            <RefreshCw className="w-3 h-3 inline mr-1" /> Refresh
          </button>
        </div>
      </div>

      {/* Stat cards */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))", gap: 8, marginBottom: 12 }}>
        <div className="vb-statcard">
          <div className="vb-statcard-label"><Wallet className="w-3 h-3 inline mr-1" />Plafon</div>
          <div className="vb-statcard-value" style={{ color: "#003399" }} data-testid="kop-plafon">{rp(plafon)}</div>
        </div>
        <div className="vb-statcard">
          <div className="vb-statcard-label">Terhutang (Aktif)</div>
          <div className="vb-statcard-value" style={{ color: "#d8000c" }} data-testid="kop-terhutang">{rp(terhutang)}</div>
        </div>
        <div className="vb-statcard">
          <div className="vb-statcard-label">Sisa Plafon</div>
          <div className="vb-statcard-value" style={{ color: sisa < 0 ? "#d8000c" : "#4f8a10" }} data-testid="kop-sisa">{rp(sisa)}</div>
        </div>
        <div className="vb-statcard">
          <div className="vb-statcard-label"><Receipt className="w-3 h-3 inline mr-1" />Jumlah Transaksi</div>
          <div className="vb-statcard-value">{(data.transaksi || []).length}</div>
        </div>
      </div>

      {/* Transaksi list */}
      <div style={{ background: "#ECE9D8", padding: 6, border: "1px solid #828790", marginBottom: 4, fontWeight: 700, fontSize: 12 }}>
        Riwayat Transaksi Koperasi
      </div>
      {(data.transaksi || []).length === 0 ? (
        <EmptyState message="Belum ada transaksi koperasi" />
      ) : (
        <div style={{ overflowX: "auto" }}>
          <table className="vb-table">
            <thead>
              <tr>
                <th style={{ width: 130 }}>No Transaksi</th>
                <th>Tanggal</th>
                <th>Vendor</th>
                <th style={{ textAlign: "right" }}>Total</th>
                <th>Metode</th>
                <th>Status</th>
                <th>Catatan</th>
              </tr>
            </thead>
            <tbody>
              {(data.transaksi || []).map((t) => (
                <tr key={t.id} data-testid={`kop-trx-${t.id}`}>
                  <td style={{ fontFamily: "Consolas, monospace", fontSize: 11 }}>{t.no_transaksi}</td>
                  <td style={{ fontSize: 11 }}>{(t.created_at || t.tanggal || "").slice(0, 16).replace("T", " ")}</td>
                  <td>{t.vendor_nama || "-"}</td>
                  <td style={{ textAlign: "right", fontWeight: 700 }}>{rp(t.total)}</td>
                  <td>
                    <span className="vb-badge vb-badge-izin">{t.metode_bayar || "Hutang"}</span>
                  </td>
                  <td>
                    {t.status === "Lunas" ? (
                      <span className="vb-badge vb-badge-approved">Lunas</span>
                    ) : t.status === "Terhutang" ? (
                      <span className="vb-badge vb-badge-rejected">Terhutang</span>
                    ) : (
                      <span className="vb-badge vb-badge-pending">{t.status}</span>
                    )}
                  </td>
                  <td style={{ fontSize: 11, color: "#555" }}>{t.catatan || "-"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Window>
  );
}
